// @flow strict
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {AnalyticsSurveyAndNPSActions} from 'src/action-creators//analytics/analytics-survey-and-nps';
import type {
  SelectedEntity,
  CurrentFilters,
  SavedDashboardList,
  SelectedSurveyNpsFilterColumnsData,
  SelectedQuestionFilters,
} from 'src/types/analytics-genesis';

import {generateUUID} from 'src/utils/page-site';
import {
  CLEAR_DRILLDOWN_SELECTED_FILTERS,
  CLEAR_SELECTED_FILTERS,
  SET_SELECTED_DATE_RANGE,
  RECEIVE_SAVED_DASHBOARDS,
  SET_CURRENT_DASHBOARD_ID,
  SET_SELECTED_ENTITIES_FILTER,
  SET_SELECTED_JOURNEY_FILTERS,
  RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS,
  RECEIVE_CLIENT_NPS_TABLE_COLUMNS,
  RECEIVE_ALL_SURVEYS_TABLE_COLUMNS,
  RECEIVE_CONVERSION_SUMMARY_TABLE_COLUMNS,
  SET_SELECTED_JOURNEY_FILTERS_SURVEY_DRILLDOWN,
  SET_SELECTED_ENTITIES_FILTER_SURVEY_DRILLDOWN,
  SET_SELECTED_QUESTIONS_FILTER_SURVEY_DRILLDOWN,
  RECEIVE_CANDIDATE_NPS_CHART_COLUMNS,
  RECEIVE_CLIENT_NPS_CHART_COLUMNS,
  RECEIVE_ALL_RESPONSES_TABLE_COLUMNS,
  SET_SURVEY_DRILLDOWN_DASHBOARD_ID,
  SET_NAVIGATION_DOWN,
  SET_NAVIGATION_UP,
  RECEIVE_SAVED_DRILLDOWN_DASHBOARDS,
  SET_SELECTED_DATE_RANGE_DRILLDOWN,
} from 'src/action-creators/analytics/analytics-survey-and-nps';


export type AnalyticsSurveyAndNPSState = {
  savedDashboards: SavedDashboardList | null,
  savedDrilldownDashboards: SavedDashboardList | null,
  selectedDateRange: DateRangeWithTimezone,
  selectedDateRangeDrilldown: DateRangeWithTimezone,
  currentDashboardID: ?string,
  selectedJourneyFilters: CurrentFilters,
  selectedEntitiesFilters: Array<SelectedEntity>,
  candidateNPSTableColumnFilter: SelectedSurveyNpsFilterColumnsData | null,
  clientNPSTableColumnFilter: SelectedSurveyNpsFilterColumnsData | null,
  allSurveysTableColumnFilter: SelectedSurveyNpsFilterColumnsData | null,
  conversionSummaryTableColumnFilter: SelectedSurveyNpsFilterColumnsData | null,
  selectedJourneyFiltersSurveyDrilldown: CurrentFilters,
  selectedEntitiesFiltersSurveyDrilldown: Array<SelectedEntity>,
  selectedQuestionsFiltersSurveyDrilldown: Array<SelectedQuestionFilters>,
  candidateNPSChartColumnFilter: Array<string> | null,
  clientNPSChartColumnFilter: Array<string> | null,
  allResponsesTableColumnFilter: SelectedSurveyNpsFilterColumnsData | null,
  surveyDrilldownDashboardID: ?string,
  isNavigationDown: boolean | null,
  isNavigationUp: boolean | null,
};

const initialState: AnalyticsSurveyAndNPSState = {
  selectedDateRange: {
    startDateUTC: null,
    startDate: null,
    endDateUTC: null,
    endDate: null,
  },
  selectedDateRangeDrilldown: {
    startDateUTC: null,
    startDate: null,
    endDateUTC: null,
    endDate: null,
  },
  currentDashboardID: null,
  selectedJourneyFilters: {
    journey_lifecycle_goal: [],
    journey_business_goal: [],
    journey_category: [],
    journey_status: [],
    journeys: [],
    question_types: [],
  },
  selectedEntitiesFilters: [{id: generateUUID()}],
  savedDashboards: null,
  savedDrilldownDashboards: null,
  candidateNPSTableColumnFilter: null,
  clientNPSTableColumnFilter: null,
  allSurveysTableColumnFilter: null,
  conversionSummaryTableColumnFilter: null,
  selectedJourneyFiltersSurveyDrilldown: {
    journey_lifecycle_goal: [],
    journey_business_goal: [],
    journey_category: [],
    journey_status: [],
    journeys: [],
    question_types: [],
  },
  selectedEntitiesFiltersSurveyDrilldown: [{id: generateUUID()}],
  selectedQuestionsFiltersSurveyDrilldown: [{id: generateUUID()}],
  candidateNPSChartColumnFilter: null,
  clientNPSChartColumnFilter: null,
  allResponsesTableColumnFilter: null,
  surveyDrilldownDashboardID: null,
  isNavigationDown: null,
  isNavigationUp: null,
};

export default (
  state: AnalyticsSurveyAndNPSState = initialState,
  action: AnalyticsSurveyAndNPSActions,
): AnalyticsSurveyAndNPSState => {
  switch (action.type) {
    case CLEAR_SELECTED_FILTERS: {
      return {
        ...state,
        selectedJourneyFilters: {
          journey_lifecycle_goal: [],
          journey_business_goal: [],
          journey_category: [],
          journey_status: [],
          journeys: [],
          question_types: [],
        },
        selectedEntitiesFilters: [{id: generateUUID()}],
        selectedDateRange: {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
      };
    }
    case CLEAR_DRILLDOWN_SELECTED_FILTERS: {
      return {
        ...state,
        selectedEntitiesFiltersSurveyDrilldown: [{id: generateUUID()}],
        selectedQuestionsFiltersSurveyDrilldown: [{id: generateUUID()}],
        selectedJourneyFiltersSurveyDrilldown: {
          journey_lifecycle_goal: [],
          journey_business_goal: [],
          journey_category: [],
          journey_status: [],
          journeys: [],
          question_types: [],
        },
        selectedDateRangeDrilldown: {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
      };
    }
    case SET_CURRENT_DASHBOARD_ID: {
      return {
        ...state,
        currentDashboardID: action.payload,
      };
    }
    case SET_SURVEY_DRILLDOWN_DASHBOARD_ID: {
      return {
        ...state,
        surveyDrilldownDashboardID: action.payload,
      };
    }
    case SET_SELECTED_ENTITIES_FILTER: {
      return {
        ...state,
        selectedEntitiesFilters: action.payload,
      };
    }
    case SET_SELECTED_ENTITIES_FILTER_SURVEY_DRILLDOWN: {
      return {
        ...state,
        selectedEntitiesFiltersSurveyDrilldown: action.payload,
      };
    }
    case SET_SELECTED_JOURNEY_FILTERS: {
      const {selectedJourneyFilters} = state;
      return {
        ...state,
        selectedJourneyFilters: {
          ...selectedJourneyFilters,
          ...action.payload,
        },
      };
    }
    case SET_SELECTED_JOURNEY_FILTERS_SURVEY_DRILLDOWN: {
      const {selectedJourneyFiltersSurveyDrilldown} = state;
      return {
        ...state,
        selectedJourneyFiltersSurveyDrilldown: {
          ...selectedJourneyFiltersSurveyDrilldown,
          ...action.payload,
        },
      };
    }
    case SET_SELECTED_DATE_RANGE: {
      return {
        ...state,
        selectedDateRange: action.payload,
      };
    }
    case SET_SELECTED_DATE_RANGE_DRILLDOWN: {
      return {
        ...state,
        selectedDateRangeDrilldown: action.payload,
      };
    }
    case RECEIVE_SAVED_DASHBOARDS: {
      return {
        ...state,
        savedDashboards: action.payload,
      };
    }
    case RECEIVE_SAVED_DRILLDOWN_DASHBOARDS: {
      return {
        ...state,
        savedDrilldownDashboards: action.payload,
      };
    }
    case RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS: {
      return {...state, candidateNPSTableColumnFilter: action.payload};
    }
    case RECEIVE_CLIENT_NPS_TABLE_COLUMNS: {
      return {...state, clientNPSTableColumnFilter: action.payload};
    }
    case RECEIVE_ALL_SURVEYS_TABLE_COLUMNS: {
      return {...state, allSurveysTableColumnFilter: action.payload};
    }
    case RECEIVE_CONVERSION_SUMMARY_TABLE_COLUMNS: {
      return {...state, conversionSummaryTableColumnFilter: action.payload};
    }
    case SET_SELECTED_QUESTIONS_FILTER_SURVEY_DRILLDOWN: {
      return {
        ...state,
        selectedQuestionsFiltersSurveyDrilldown: action.payload,
      };
    }
    case RECEIVE_CANDIDATE_NPS_CHART_COLUMNS: {
      return {...state, candidateNPSChartColumnFilter: action.payload};
    }
    case RECEIVE_CLIENT_NPS_CHART_COLUMNS: {
      return {...state, clientNPSChartColumnFilter: action.payload};
    }
    case RECEIVE_ALL_RESPONSES_TABLE_COLUMNS: {
      return {...state, allResponsesTableColumnFilter: action.payload};
    }
    case SET_NAVIGATION_DOWN: {
      return {
        ...state,
        isNavigationDown: action.payload,
      };
    }
    case SET_NAVIGATION_UP: {
      return {
        ...state,
        isNavigationUp: action.payload,
      };
    }
  }
  return state;
};
